import { Box, Typography } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { useState, useEffect, useRef } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { startSendGlobalAgentMessage } from "../../../actions/chat";
import {
  DATE_LONG,
  GLOBAL_REPORT_SUMMARY_NOT_GENERATED,
  GLOBAL_REPORT_SUMMARY_OVERVIEW_TEXT,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";

const SUMMARY_SKELETON_LINES = [
  { width: "100%" },
  { width: "98%" },
  { width: "94%" },
  { width: "88%" },
  { width: "72%" },
];

const GlobalReportSummarySkeleton = () => (
  <Box
    aria-busy="true"
    aria-label="loading summary"
    sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
  >
    {SUMMARY_SKELETON_LINES.map((line, index) => (
      <Skeleton
        key={`summary-skeleton-line-${index}`}
        variant="text"
        width={line.width}
        height={24}
        sx={{ transform: "none" }}
      />
    ))}
  </Box>
);

const GlobalReportSummarySection = ({
  globalReportContext,
  agentRequestKey,
  isReady = false,
  onSummaryGenerated = () => {},
  onAiSettledChange = () => {},
}) => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [generatedText, setGeneratedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const fetchIdRef = useRef(0);
  const lastFetchedKeyRef = useRef(null);

  useEffect(() => {
    if (!isReady || !globalReportContext || !agentRequestKey) {
      onAiSettledChange(false);
      return;
    }

    const agentFetchKey = `${agentRequestKey}|${i18n.language}`;
    if (lastFetchedKeyRef.current === agentFetchKey) {
      onAiSettledChange(true);
      return;
    }
    lastFetchedKeyRef.current = agentFetchKey;

    const fetchId = ++fetchIdRef.current;
    const generateSummary = async () => {
      setIsLoading(true);
      setGeneratedText("");

      try {
        const response = await dispatch(
          startSendGlobalAgentMessage({
            messages: [
              {
                role: "user",
                content: t(GLOBAL_REPORT_SUMMARY_OVERVIEW_TEXT, {
                  startDateTime: t(DATE_LONG, {
                    date: new Date(globalReportContext.startDateTime),
                  }),
                  endDateTime: t(DATE_LONG, {
                    date: new Date(globalReportContext.endDateTime),
                  }),
                }),
              },
            ],
            globalReportContext,
          })
        );

        if (fetchId !== fetchIdRef.current) {
          return;
        }

        if (response?.ok && response?.data?.[0]?.answer) {
          const answer = response.data[0].answer;
          setGeneratedText(answer);
          onSummaryGenerated(answer);
        } else {
          onSummaryGenerated("");
        }
      } catch (error) {
        console.error("Error generating global report summary:", error);
        onSummaryGenerated("");
      } finally {
        if (fetchId === fetchIdRef.current) {
          setIsLoading(false);
          onAiSettledChange(true);
        }
      }
    };

    generateSummary();
  }, [
    agentRequestKey,
    dispatch,
    globalReportContext,
    i18n.language,
    isReady,
    onAiSettledChange,
    onSummaryGenerated,
    t,
  ]);

  useEffect(() => {
    return () => onAiSettledChange(false);
  }, [onAiSettledChange]);

  if (!globalReportContext) {
    return null;
  }

  return (
    <Box
      id="global-report-summary-section"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
      }}
    >
      {isLoading || !isReady ? (
        <GlobalReportSummarySkeleton />
      ) : generatedText ? (
        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
          {generatedText}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t(GLOBAL_REPORT_SUMMARY_NOT_GENERATED)}
        </Typography>
      )}
    </Box>
  );
};

export default GlobalReportSummarySection;
